import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ProjectOverlay.css';
import Marketplace from '../projects/Marketplace';
import AIProxy from '../projects/AIProxy';
import GolfGoals from '../projects/GolfGoals';
import Letterfall from '../projects/Letterfall';
import ABCFinder from '../projects/ABCFinder';
import SwiftUI from '../projects/SwiftUI';
import Facebook4D from '../projects/Facebook';
import Joypad from '../projects/Joypad';
import Figma from '../projects/Figma';
import Archive from '../projects/Archive';
import About from '../projects/About';
import { HugeiconsIcon } from '@hugeicons/react';
import { CancelIcon, InformationCircleIcon } from '@hugeicons-pro/core-stroke-sharp';
import { projectMeta } from '../projects/data';
import Button from './Button';

export default function ProjectOverlay({ open, projectId, onClose, enterDelay = 0, onExitComplete }) {

  // Custom cursor state (only on overlay and only over media)
  const [cursorText, setCursorText] = React.useState('');
  const [cursorVisible, setCursorVisible] = React.useState(false);
  const [cursorPos, setCursorPos] = React.useState({ x: 0, y: 0 });

  // Quick toggle to disable the custom cursor entirely
  const overlayCursorEnabled = false;

  // Attach hover handlers to media when overlay is open or project changes
  React.useEffect(() => {
    if (!open || !overlayCursorEnabled) return;
    const root = document.getElementById(projectId ? `project-${projectId}` : undefined) || document;
    if (!root) return;

    const getCaptionForNode = (node) => {
      if (!node) return '';
      const figure = node.closest && node.closest('.figure');
      const captionEl = figure ? figure.querySelector('.figure-caption') : null;
      if (captionEl && captionEl.textContent) return captionEl.textContent;
      const mediaEl = figure ? (figure.querySelector('img.gallery-item, video.gallery-item')) : (node.matches && node.matches('img.gallery-item, video.gallery-item') ? node : null);
      if (mediaEl) {
        return mediaEl.getAttribute('data-caption') || mediaEl.getAttribute('alt') || '';
      }
      return '';
    };

    const targets = Array.from(root.querySelectorAll('.video-mask, img.gallery-item, video.gallery-item'));
    const listeners = [];
    targets.forEach((t) => {
      const onEnter = (e) => {
        try {
          const caption = getCaptionForNode(t);
          if (!caption) return;
          // t.style.cursor = 'none';
          // Set position first to avoid fly-in from 0,0
          setCursorPos({ x: e.clientX + 12, y: e.clientY + 12 });
          setCursorText(caption);
          setCursorVisible(true);
        } catch (_) {}
      };
      const onMove = (e) => {
        try { setCursorPos({ x: e.clientX + 12, y: e.clientY + 12 }); } catch (_) {}
      };
      const onLeave = () => {
        // try { t.style.cursor = ''; } catch (_) {}
        setCursorVisible(false);
        setCursorText('');
      };
      t.addEventListener('mouseenter', onEnter);
      t.addEventListener('mousemove', onMove);
      t.addEventListener('mouseleave', onLeave);
      listeners.push({ t, onEnter, onMove, onLeave });
    });

    return () => {
      listeners.forEach(({ t, onEnter, onMove, onLeave }) => {
        try {
          t.removeEventListener('mouseenter', onEnter);
          t.removeEventListener('mousemove', onMove);
          t.removeEventListener('mouseleave', onLeave);
        } catch (_) {}
      });
    };
  }, [open, projectId, overlayCursorEnabled]);

  // Lock page scroll when overlay opens; unlock only after exit completes to avoid double scrollbars
  const unlockScroll = React.useCallback(() => {
    document.body.style.overflow = '';
    try { document.body.classList.remove('overlay-open'); } catch(_) {}
    try { document.documentElement.classList.remove('overlay-open-root'); } catch(_) {}
  }, []);

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      try { document.body.classList.add('overlay-open'); } catch(_) {}
      try { document.documentElement.classList.add('overlay-open-root'); } catch(_) {}
    }
    return () => {
      // If component unmounts unexpectedly, ensure unlock
      unlockScroll();
    };
  }, [open, unlockScroll]);

  // Preload first image for the current project to avoid initial flicker
  React.useEffect(() => {
    if (!projectId) return;
    try {
      const first = projectMeta[projectId]?.firstImage;
      if (first) {
        const img = new Image();
        img.decoding = 'async';
        img.loading = 'eager';
        img.src = first;
      }
    } catch(_) {}
  }, [projectId]);

  // Signal when overlay content has fully entered to allow deferred media loading
  React.useEffect(() => {
    if (!open) return;
    const ms = Math.max(0, Math.round((enterDelay + 0.5) * 1000));
    const t = setTimeout(() => {
      try {
        window.__overlayEntered = true;
        window.dispatchEvent(new CustomEvent('overlayEntered'));
      } catch (_) {}
    }, ms);
    return () => clearTimeout(t);
  }, [open, enterDelay]);

  // After overlay enters, preload above-the-fold images and video posters for current project
  React.useEffect(() => {
    if (!open || !projectId) return;
    const rootId = projectId ? `project-${projectId}` : undefined;
    const run = () => {
      try {
        const root = document.getElementById(rootId) || document;
        if (!root) return;
        const viewport = root.querySelector('.project-body') || root;
        const viewTop = 0;
        const viewHeight = (viewport.clientHeight || window.innerHeight || 0);
        const viewBottom = viewTop + viewHeight + 800; // buffer

        const items = Array.from(root.querySelectorAll('img.gallery-item, video.gallery-item'));
        const urls = [];
        items.forEach((el) => {
          if (el.tagName === 'IMG') {
            const src = el.getAttribute('src');
            if (src) urls.push({ url: src, el });
          } else if (el.tagName === 'VIDEO') {
            const poster = el.getAttribute('poster');
            if (poster) urls.push({ url: poster, el });
          }
        });

        // Prioritize items likely in view first (by DOM position)
        const MAX_INITIAL = 8;
        const toPreload = urls.slice(0, MAX_INITIAL);
        toPreload.forEach(({ url }) => {
          try {
            const img = new Image();
            img.decoding = 'async';
            img.loading = 'eager';
            img.src = url;
          } catch (_) {}
        });
      } catch (_) {}
    };

    if (typeof window !== 'undefined' && window.__overlayEntered) {
      // Slight deferral to avoid competing with entrance paint
      const id = requestAnimationFrame(run);
      return () => cancelAnimationFrame(id);
    } else if (typeof window !== 'undefined') {
      const onEntered = () => run();
      window.addEventListener('overlayEntered', onEntered, { once: true });
      return () => window.removeEventListener('overlayEntered', onEntered);
    }
  }, [open, projectId]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        try { unlockScroll(); } catch(_) {}
        if (onExitComplete) onExitComplete();
      }}
    >
      {open && (
        <motion.div
          className="project-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1.0, transition: { duration: 0.5, delay: enterDelay } }}
          exit={{ opacity: 0, transition: { duration: 0.2, delay: 0 } }}
        >
          <Button
            className="overlay-close"
            square
            hoverBounce
            ariaLabel="Close"
            onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
            leading={<HugeiconsIcon icon={CancelIcon} size={24} strokeWidth={1.5} color="currentColor" />}
          />
          <motion.div
            id={projectId ? `project-${projectId}` : undefined}
            className={`project-content project-regular ${projectId === 'swiftui' || projectId === 'archive' ? 'project-wide' : ''}`}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { duration: 0.5, ease: 'easeOut', delay: enterDelay } }}
            exit={{ y: 24, opacity: 0, transition: { duration: 0.2, ease: 'easeIn', delay: 0 } }}
            role="dialog"
            aria-modal="true"
            aria-label={projectMeta[projectId]?.title || 'Project details'}
            onClick={(e) => e.stopPropagation()}
          >

            {projectId === 'marketplace' && <Marketplace />}
            {projectId === 'aiproxy' && <AIProxy />}
            {projectId === 'golfgoals' && <GolfGoals />}
            {projectId === 'letterfall' && <Letterfall />}
            {projectId === 'abc' && <ABCFinder />}
            {projectId === 'swiftui' && <SwiftUI />}
            {projectId === 'facebook' && <Facebook4D />}
            {projectId === 'joypad' && <Joypad />}
            {projectId === 'figma' && <Figma />}
            {projectId === 'archive' && <Archive />}
            {projectId === 'about' && <About />}
            {!projectId && (
              <div className="project-body">
                <h2 className="project-title">Project</h2>
                <p className="project-desc">Select a project to view details.</p>
              </div>
            )}

          </motion.div>

          {/* Custom cursor bubble for media captions */}
          <AnimatePresence>
            {overlayCursorEnabled && cursorVisible && cursorText && (
              <motion.div
                className="overlay-cursor"
                initial={{ opacity: 0, scale: 0.5, x: cursorPos.x, y: cursorPos.y }}
                animate={{ opacity: 1, scale: 1, x: cursorPos.x, y: cursorPos.y }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ type: 'spring', stiffness: 380, damping: 36, mass: 0.3 }}
              >
                <HugeiconsIcon icon={InformationCircleIcon} size={10} color="currentColor" />
                <span className="overlay-cursor-text">{cursorText}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


